/**
 * SOVRANOVA — AmbientView (Ambient Companion, Wave 1: self debrief)
 * ------------------------------------------------------------
 * Speak or type a debrief of a therapy session or a hard conversation (your
 * own words); SOVRANOVA reflects it back: summary, themes, key moments, and a
 * warm reflection. Self-only. Transcription reuses /api/voice (Whisper);
 * reflection calls /api/ambient/reflect. Gated by VITE_FEATURE_AMBIENT.
 *
 * While recording, a "session mode" overlay dims the screen to near-black and a
 * screen wake lock keeps the page (and the mic) alive — the web "screen-off
 * feel" so a long in-person debrief isn't cut off when the screen would dim.
 */
import { useState, useRef, useEffect, useCallback } from 'react'
import { api } from '../../services/api'
import { ConsentFlow } from './ConsentFlow'
import { ReflectionsHistory } from './ReflectionsHistory'
import { SelfOnlyConsent } from './SelfOnlyConsent'
import { useSettings } from '../../hooks/useSettings'
import { speakLong } from '../../services/speech'
import { ReflectionThread } from './ReflectionThread'
import { CaptureView } from './CaptureView'
import { bufferPut, bufferDelete, bufferList, clipId } from '../../services/audioBuffer'

// Wave 2 all-party consent screen (records consent only — enables NO capture).
// Distinct from SelfOnlyConsent (self-side capture acknowledgement, no gate).
const FEATURE_AMBIENT_CONSENT = import.meta.env.VITE_FEATURE_AMBIENT_CONSENT === 'true'

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`

// A complete audio file every ~3 minutes. The 13 Aug lesson, learned the hard
// way on a real 60-minute therapy session: a single-take recording produces one
// giant blob that exceeds the upload limit and loses EVERYTHING. Cycled takes
// mean each few minutes transcribes (and lands in the box) independently — a
// long debrief can no longer fail as a whole, only a single cycle can, and a
// failed cycle is kept for retry instead of vanishing.
const CYCLE_MS = 3 * 60 * 1000

// Gentle openers for a first-time (or blank-page) user. Tapping one seeds the box.
const STARTERS = [
  { label: 'Just had a session',          text: 'I just had a therapy session, and ' },
  { label: 'A hard conversation',         text: 'A hard conversation happened, and ' },
  { label: "Something's sitting with me", text: "Something's been sitting with me, and " },
]

export function AmbientView({ onClose, onHome, onOpenSession }) {
  const [text, setText]                 = useState('')
  const [recording, setRecording]       = useState(false)
  const [pendingTx, setPendingTx]       = useState(0)     // cycle transcriptions in flight
  const [failedClips, setFailedClips]   = useState([])    // [{id, blob, mimeType}] — kept, retryable
  const [retrying, setRetrying]         = useState(false)
  const [recoverable, setRecoverable]   = useState([])    // device-buffered clips from an earlier visit
  const transcribing = pendingTx > 0
  const [reflecting, setReflecting]     = useState(false)
  const [result, setResult]             = useState(null)
  const [error, setError]               = useState(null)
  const [elapsed, setElapsed]           = useState(0)
  const [showConsent, setShowConsent]   = useState(false)
  // THE LIBRARY IS THE FRONT DOOR (19 Aug): opening Reflect lands on "Your
  // reflections" first — pick the reflection to continue (its recordings and
  // thread stay archived with it) or explicitly start a new one. Landing
  // straight on the record screen made people record debriefs that belonged
  // with an existing reflection.
  const [showHistory, setShowHistory]   = useState(true)
  const [showSelfConsent, setShowSelfConsent] = useState(false)
  const [capture, setCapture]           = useState(null) // { consentId, names } — live consent-gated capture
  const [repair, setRepair]             = useState(null)
  const [repairLoading, setRepairLoading] = useState(false)
  const [welcome, setWelcome]           = useState(null)

  const [speaking, setSpeaking]         = useState(false)

  const mediaRecorderRef = useRef(null)
  const chunksRef        = useRef([])
  const streamRef        = useRef(null)
  const cycleTimerRef    = useRef(null)
  const runningRef       = useRef(false)   // should a finished cycle start another?
  const wakeLockRef      = useRef(null)
  const timerRef         = useRef(null)
  const ttsRef           = useRef(null)
  const speakTokenRef    = useRef(0)

  const { settings } = useSettings()
  const voiceOff = settings.textOnly || !settings.voiceEnabled

  // ── The reflection is SPOKEN, not just printed ───────────────────────────
  // A reflection read off a screen is a report. The same words in a warm voice
  // land like a person saying them back to you, which is the entire point of
  // reflecting after a session or a hard conversation. Uses the full chunked
  // path, so a long reflection is spoken whole rather than cut short.
  //
  // Only the reflection itself is voiced — the summary, themes and key moments
  // stay as text on purpose. They're for scanning and for tracking progress
  // over time, and reading a bullet list aloud sounds like an audit, not care.
  const speakReflection = useCallback(async (textToSpeak) => {
    if (!textToSpeak || voiceOff) return
    const token = ++speakTokenRef.current
    setSpeaking(true)
    try {
      await speakLong(textToSpeak, ttsRef, () => speakTokenRef.current !== token)
    } catch { /* voice is never fatal — the words are on screen regardless */ }
    if (speakTokenRef.current === token) setSpeaking(false)
  }, [voiceOff])

  const hush = useCallback(() => {
    speakTokenRef.current++
    try { ttsRef.current?.pause() } catch { /* noop */ }
    setSpeaking(false)
  }, [])

  // Never let the companion keep talking after this screen is gone, and stop
  // the moment voice is switched off in settings.
  useEffect(() => () => { speakTokenRef.current++; try { ttsRef.current?.pause() } catch {} }, [])
  useEffect(() => { if (voiceOff) hush() }, [voiceOff, hush])

  // On open, fetch a warm callback to the last reflection (non-blocking, self-only).
  useEffect(() => {
    let alive = true
    api.get('/ambient/welcome').then(r => { if (alive && r?.callback) setWelcome(r.callback) }).catch(() => {})
    return () => { alive = false }
  }, [])

  // Keep the screen awake (dimmed) during a debrief so the page — and the mic —
  // keep running. Non-fatal if the browser denies it.
  const acquireWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator && !wakeLockRef.current) {
        wakeLockRef.current = await navigator.wakeLock.request('screen')
        wakeLockRef.current.addEventListener?.('release', () => { wakeLockRef.current = null })
      }
    } catch { /* non-fatal */ }
  }, [])
  const releaseWakeLock = useCallback(() => {
    try { wakeLockRef.current?.release?.() } catch { /* noop */ }
    wakeLockRef.current = null
  }, [])

  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null } }

  const stopRecording = useCallback(() => {
    runningRef.current = false
    if (cycleTimerRef.current) { clearTimeout(cycleTimerRef.current); cycleTimerRef.current = null }
    setRecording(prev => {
      if (prev && mediaRecorderRef.current) { try { mediaRecorderRef.current.stop() } catch { /* noop */ } }
      return false
    })
    try { streamRef.current?.getTracks().forEach(t => t.stop()) } catch { /* noop */ }
    streamRef.current = null
    stopTimer()
    releaseWakeLock()
  }, [releaseWakeLock])

  // If the tab is hidden mid-recording the browser suspends the mic — stop and
  // keep what we captured. Clean up the timer + lock on unmount.
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'hidden') stopRecording() }
    document.addEventListener('visibilitychange', onVis)
    return () => {
      document.removeEventListener('visibilitychange', onVis)
      stopTimer()
      releaseWakeLock()
    }
  }, [stopRecording, releaseWakeLock])

  // Transcribe one complete cycle file and append it to the box. The audio is
  // ALREADY in the on-device safety buffer before this runs; on confirmed
  // transcription the buffered copy deletes itself (the 13 Aug rule: audio
  // lives only until its words are safely written down). A clip that fails
  // twice is KEPT — in state for this visit AND on the device across visits.
  const transcribeClip = useCallback(async (blob, mimeType, id) => {
    if (!blob || !blob.size) { if (id) bufferDelete(id); return }
    setPendingTx(n => n + 1)
    try {
      const base64 = await new Promise((res, rej) => {
        const reader = new FileReader()
        reader.onload = () => res(reader.result.split(',')[1])
        reader.onerror = rej
        reader.readAsDataURL(blob)
      })
      const doTranscribe = () => api.post('/voice/transcribe', { audio: base64, mimeType })
      let transcript
      try { ({ text: transcript } = await doTranscribe()) }
      catch { await new Promise(r => setTimeout(r, 800)); ({ text: transcript } = await doTranscribe()) }
      if (transcript) setText(prev => (prev ? `${prev} ${transcript}` : transcript))
      if (id) bufferDelete(id)          // words saved -> the audio has done its job
      return true
    } catch (err) {
      setFailedClips(prev => [...prev, { id, blob, mimeType }])   // device copy stays
      setError(err.message?.includes('not configured')
        ? 'Voice transcription needs OPENAI_API_KEY on the backend — or just type below.'
        : "A few minutes of audio couldn't be transcribed — it's kept safely on this device, tap Retry below.")
      return false
    } finally { setPendingTx(n => Math.max(0, n - 1)) }
  }, [])

  const retryFailedClips = useCallback(async () => {
    if (retrying) return
    setRetrying(true)
    setError(null)
    const clips = failedClips
    setFailedClips([])
    for (const c of clips) { await transcribeClip(c.blob, c.mimeType, c.id) }
    setRetrying(false)
  }, [failedClips, retrying, transcribeClip])

  // ── Recovery of clips buffered on an EARLIER visit ────────────────────────
  // If the app crashed, the battery died or a transcription failed and the
  // page was closed, the audio is still here on the device. Offer it back.
  useEffect(() => {
    let alive = true
    bufferList().then(clips => {
      if (!alive || !clips.length) return
      setRecoverable(clips)
      // Safety net outranks the library-first flow: buffered audio from an
      // interrupted recording must be SEEN, so open the record screen where
      // the recovery banner lives rather than leaving it behind the list.
      setShowHistory(false)
    }).catch(() => {})
    return () => { alive = false }
  }, [])

  const recoverBuffered = useCallback(async () => {
    if (retrying) return
    setRetrying(true)
    setError(null)
    const clips = [...recoverable].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
    setRecoverable([])
    for (const c of clips) { await transcribeClip(c.blob, c.mimeType, c.id) }
    setRetrying(false)
  }, [recoverable, retrying, transcribeClip])

  const discardBuffered = useCallback(async () => {
    const clips = recoverable
    setRecoverable([])
    for (const c of clips) { await bufferDelete(c.id) }
  }, [recoverable])

  const pickMime = () =>
    MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
      : MediaRecorder.isTypeSupported('audio/ogg;codecs=opus') ? 'audio/ogg;codecs=opus'
      : 'audio/mp4'

  // One recording CYCLE — a complete standalone audio file (~3 min). When it
  // ends (timer or user stop), it transcribes in the background and its words
  // land in the box while the next cycle keeps recording. See CYCLE_MS above.
  const beginCycle = useCallback(() => {
    const stream = streamRef.current
    if (!stream || !runningRef.current) return
    const mimeType = pickMime()
    const recorder = new MediaRecorder(stream, { mimeType })
    const localChunks = []
    recorder.ondataavailable = e => { if (e.data.size > 0) localChunks.push(e.data) }
    recorder.onerror = () => { try { recorder.stop() } catch { /* noop */ } }
    recorder.onstop = () => {
      const blob = new Blob(localChunks, { type: recorder.mimeType })
      const id = clipId()
      // Safety-buffer FIRST, then transcribe: from this moment the audio
      // survives anything — crash, reload, dead battery — until its words
      // are confirmed saved, at which point the buffered copy self-deletes.
      bufferPut(id, blob, recorder.mimeType, 'debrief').finally(() => {
        transcribeClip(blob, recorder.mimeType, id)   // async — recording continues
      })
      if (runningRef.current) beginCycle()
    }
    mediaRecorderRef.current = recorder
    recorder.start(1000)
    if (cycleTimerRef.current) clearTimeout(cycleTimerRef.current)
    cycleTimerRef.current = setTimeout(() => { try { recorder.stop() } catch { /* noop */ } }, CYCLE_MS)
  }, [transcribeClip])

  const startRecording = async () => {
    setError(null)
    acquireWakeLock()
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true })
      runningRef.current = true
      setRecording(true)
      setElapsed(0)
      stopTimer()
      timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000)
      beginCycle()
    } catch (err) {
      releaseWakeLock()
      setError(err.name === 'NotAllowedError' ? 'Microphone permission denied.' : 'Could not access the microphone.')
    }
  }

  const reflect = async () => {
    const debrief = text.trim()
    if (debrief.length < 2 || reflecting) return
    setError(null); setReflecting(true); setResult(null)
    try {
      const r = await api.post('/ambient/reflect', { text: debrief })
      setResult(r)
      // Say it back to them. Crisis responses stay silent and unchanged —
      // that path shows resources that may need to be read, kept private from
      // anyone else in the room, and acted on rather than listened to.
      if (!r?.crisis) speakReflection(r?.reflection)
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again in a moment.')
    } finally { setReflecting(false) }
  }

  const draftRepair = async () => {
    if (repairLoading) return
    setRepairLoading(true)
    try { const r = await api.post('/ambient/repair', { text: text.trim() }); setRepair(r?.repair || 'none') }
    catch { setRepair('none') }
    finally { setRepairLoading(false) }
  }

  const reset = () => { hush(); setResult(null); setText(''); setError(null); setRepair(null) }
  const busy = recording || transcribing || reflecting

  if (capture) return (
    <CaptureView
      consentId={capture.consentId}
      names={capture.names}
      onExit={(opts) => { setCapture(null); if (opts?.openHistory) setShowHistory(true) }}
    />
  )
  if (showConsent) return (
    <ConsentFlow
      onBack={() => setShowConsent(false)}
      onBeginCapture={(consentId, names) => { setShowConsent(false); setCapture({ consentId, names }) }}
    />
  )
  if (showHistory) return (
    <ReflectionsHistory
      onBack={onClose}
      onOpenSession={onOpenSession}
      onNewReflection={() => setShowHistory(false)}
    />
  )
  if (showSelfConsent) return <SelfOnlyConsent onBack={() => setShowSelfConsent(false)} onAcknowledge={() => { setShowSelfConsent(false); startRecording() }} />

  return (
    <div style={s.wrap}>
      {/* Session mode — immersive dark capture screen while recording */}
      {recording && (
        <div style={s.sessionMode}>
          <style>{'@keyframes sovpulse{0%,100%{opacity:.35;transform:scale(1)}50%{opacity:1;transform:scale(1.3)}}'}</style>
          <div style={s.pulseDot} />
          <p style={s.listening}>Listening…</p>
          <p style={s.timer}>{fmt(elapsed)}</p>
          <p style={s.sessionHint}>The screen stays on so I keep hearing you. Speak freely — your words save every few minutes as you go, and everything is written down when you tap stop.</p>
          <button style={s.stopBig} onClick={stopRecording}>■ Stop</button>
        </div>
      )}

      <div style={{ ...s.top, justifyContent:'space-between' }}>
        {/* hush() first: this swaps the view without unmounting, so without it
            the reflection would keep talking over the history screen. */}
        <button style={s.back} onClick={() => { hush(); setShowHistory(true) }}>← Your reflections</button>
        <button style={s.back} onClick={onClose}>⌂ Journey</button>
      </div>

      <div style={s.head}>
        <div style={s.icon}>🪞</div>
        <h1 style={s.title}>Reflect</h1>
        <p style={s.sub}>Talk through a session or a hard conversation. I'll listen, then help you make sense of it — in your own time.</p>
      </div>

      {!result && recoverable.length > 0 && (
        <div style={s.recoverBanner}>
          <span style={s.retryText}>
            {recoverable.length} audio clip{recoverable.length === 1 ? '' : 's'} from an earlier recording {recoverable.length === 1 ? 'is' : 'are'} safe on this device, not yet transcribed.
          </span>
          <div style={s.recoverBtns}>
            <button style={s.retryBtn} onClick={recoverBuffered} disabled={retrying}>{retrying ? 'Recovering…' : 'Recover'}</button>
            <button style={s.discardBtn} onClick={discardBuffered} disabled={retrying}>Discard</button>
          </div>
        </div>
      )}

      {!result && welcome && (
        <div style={s.welcomeBanner}>
          <span style={s.welcomeText}>{welcome}</span>
          <button style={s.welcomeClose} onClick={() => setWelcome(null)}>×</button>
        </div>
      )}

      {!result && (
        <div style={s.card}>
          <textarea
            style={s.textarea}
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Speak or type what happened, and how it felt…"
            rows={7}
            disabled={transcribing}
          />

          {!text.trim() && !transcribing && (
            <div style={s.starters}>
              <span style={s.startersLabel}>Not sure where to start?</span>
              <div style={s.starterRow}>
                {STARTERS.map((st, i) => (
                  <button key={i} style={s.starterChip} onClick={() => setText(st.text)}>{st.label}</button>
                ))}
              </div>
            </div>
          )}
          <div style={s.controls}>
            <button
              style={{ ...s.mic, ...(recording ? s.micOn : {}) }}
              onClick={recording ? stopRecording : startRecording}
              disabled={(!recording && transcribing) || reflecting}>
              {recording ? '■ Stop' : transcribing ? 'Transcribing…' : '🎙 Record'}
            </button>
            <button style={s.reflect} onClick={reflect} disabled={busy || text.trim().length < 2}>
              {reflecting ? 'Reflecting…' : 'Reflect'}
            </button>
          </div>
          {error && <p style={s.error}>{error}</p>}
          {failedClips.length > 0 && (
            <div style={s.retryRow}>
              <span style={s.retryText}>
                {failedClips.length} clip{failedClips.length === 1 ? '' : 's'} of audio saved but not yet transcribed — nothing is lost.
              </span>
              <button style={s.retryBtn} onClick={retryFailedClips} disabled={retrying || recording}>
                {retrying ? 'Retrying…' : 'Retry'}
              </button>
            </div>
          )}
          <p style={s.privacy}>Private to you. This is your own voice note — nothing here is shared with anyone.</p>
          <button style={s.linkBtn} onClick={() => setShowSelfConsent(true)}>In a session or with others? Capture your side →</button>
        </div>
      )}

      {!result && FEATURE_AMBIENT_CONSENT && (
        <button style={s.consentEntry} onClick={() => setShowConsent(true)}>
          <span style={s.consentEntryIcon}>🤝</span>
          <span>
            <span style={s.consentEntryTitle}>Recording with others</span>
            <span style={s.consentEntrySub}>Set up all-party consent — the first step before SOVRANOVA can ever sit in on a session.</span>
          </span>
        </button>
      )}

      {result && result.crisis && (
        <div style={s.crisisCard}>
          <p style={s.crisisText}>{result.reflection}</p>
          {result.resources && (
            <div style={s.resources}>
              {['primary','text','youth','suicide','emergency'].map(k => result.resources[k] && (
                <div key={k} style={s.resource}>{result.resources[k]}</div>
              ))}
            </div>
          )}
          <button style={s.reflect} onClick={reset}>Start again</button>
        </div>
      )}

      {result && !result.crisis && (
        <div style={s.card}>
          <p style={s.reflection}>{result.reflection}</p>

          {/* The voice control sits with the reflection, not in a toolbar —
              it belongs to these words. Absent entirely when voice is off in
              settings, rather than showing a button that does nothing. */}
          {!voiceOff && result.reflection && (
            <button style={s.voiceBtn} onClick={() => (speaking ? hush() : speakReflection(result.reflection))}>
              {speaking ? '■ Stop' : '🔊 Hear this again'}
            </button>
          )}

          <p style={s.privacy}>Saved to your reflections — revisit it anytime.</p>

          {result.summary && (
            <div style={s.block}>
              <p style={s.label}>What I heard</p>
              <p style={s.summary}>{result.summary}</p>
            </div>
          )}

          {result.themes?.length > 0 && (
            <div style={s.block}>
              <p style={s.label}>Themes</p>
              <div style={s.chips}>
                {result.themes.map((t, i) => <span key={i} style={s.chip}>{t}</span>)}
              </div>
            </div>
          )}

          {result.keyMoments?.length > 0 && (
            <div style={s.block}>
              <p style={s.label}>Moments that stood out</p>
              <ul style={s.moments}>
                {result.keyMoments.map((mm, i) => <li key={i} style={s.moment}>{mm}</li>)}
              </ul>
            </div>
          )}

          {/* Keep talking about THIS reflection, right here. The old flow made
              you leave for the AI Session screen to ask a single follow-up
              question; now the conversation happens where the reflection is,
              and it's kept with it. */}
          <ReflectionThread insightId={result.insightId} voiceOff={voiceOff} />

          <div style={s.controls}>
            <button style={s.ghost} onClick={reset}>Reflect on something else</button>
            {onOpenSession && <button style={s.reflect} onClick={() => { hush(); onOpenSession({ summary: result.summary, reflection: result.reflection, debrief: text }) }}>Open a full session</button>}
          </div>

          {!repair && (
            <button style={s.repairBtn} onClick={draftRepair} disabled={repairLoading}>
              {repairLoading ? 'Finding the words…' : '💬 Draft a way to reconnect'}
            </button>
          )}
          {repair === 'none' && <p style={s.privacy}>This one doesn't look like a rupture to repair — but I'm here if it is.</p>}
          {repair && repair !== 'none' && (
            <div style={s.repairCard}>
              <p style={s.label}>A way to reconnect</p>
              <p style={s.repairText}>{repair}</p>
              <button style={s.copyBtn} onClick={() => { try { navigator.clipboard.writeText(repair) } catch { /* noop */ } }}>Copy</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const s = {
  wrap:      { minHeight:'100vh', background:'linear-gradient(165deg,#161922,#0e1119)', color:'#e8eef4', padding:'1.1rem 1.1rem 3rem', maxWidth:640, margin:'0 auto' },
  top:       { display:'flex', justifyContent:'flex-start' },
  back:      { background:'transparent', border:'none', color:'rgba(240,244,248,.6)', fontSize:'0.9rem', cursor:'pointer', padding:'0.4rem 0' },
  head:      { textAlign:'center', padding:'0.6rem 0 1.2rem' },
  icon:      { fontSize:'2.2rem' },
  title:     { margin:'0.3rem 0 0.4rem', fontSize:'1.5rem', fontWeight:800, letterSpacing:'0.02em', color:'#f0f4f8' },
  sub:       { margin:0, fontSize:'0.9rem', lineHeight:1.55, color:'rgba(240,244,248,.55)' },
  card:      { background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', borderRadius:'1rem', padding:'1.1rem', marginTop:'0.6rem' },
  textarea:  { width:'100%', boxSizing:'border-box', background:'rgba(0,0,0,.25)', border:'1px solid rgba(255,255,255,.1)', borderRadius:'0.7rem', color:'#f0f4f8', fontSize:'0.95rem', lineHeight:1.55, padding:'0.8rem', resize:'vertical', fontFamily:'inherit' },
  controls:  { display:'flex', gap:'0.6rem', marginTop:'0.8rem', flexWrap:'wrap' },
  mic:       { flex:'1 1 auto', background:'rgba(139,92,246,.15)', border:'1px solid rgba(139,92,246,.4)', color:'#d9ccff', fontSize:'0.9rem', fontWeight:700, borderRadius:'0.7rem', padding:'0.75rem 1rem', cursor:'pointer' },
  micOn:     { background:'rgba(239,68,68,.2)', border:'1px solid rgba(239,68,68,.55)', color:'#ffd9d9' },
  reflect:   { flex:'1 1 auto', background:'#8b5cf6', border:'none', color:'#fff', fontSize:'0.9rem', fontWeight:700, borderRadius:'0.7rem', padding:'0.75rem 1rem', cursor:'pointer' },
  ghost:     { flex:'1 1 auto', background:'transparent', border:'1px solid rgba(255,255,255,.18)', color:'#e8eef4', fontSize:'0.9rem', fontWeight:600, borderRadius:'0.7rem', padding:'0.75rem 1rem', cursor:'pointer' },
  voiceBtn:  { marginTop:'0.9rem', background:'rgba(139,92,246,.14)', border:'1px solid rgba(139,92,246,.34)', color:'#e6ddff', fontSize:'0.82rem', fontWeight:600, borderRadius:'1rem', padding:'0.4rem 0.9rem', cursor:'pointer', fontFamily:'inherit' },
  error:     { color:'#ffb4b4', fontSize:'0.82rem', margin:'0.7rem 0 0' },
  retryRow:  { display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.6rem', background:'rgba(250,204,21,.08)', border:'1px solid rgba(250,204,21,.3)', borderRadius:'0.7rem', padding:'0.55rem 0.75rem', marginTop:'0.7rem' },
  retryText: { fontSize:'0.8rem', lineHeight:1.45, color:'#fde68a' },
  retryBtn:  { flex:'0 0 auto', background:'rgba(250,204,21,.15)', border:'1px solid rgba(250,204,21,.45)', color:'#fde68a', fontSize:'0.82rem', fontWeight:700, borderRadius:'0.55rem', padding:'0.45rem 0.8rem', cursor:'pointer' },
  recoverBanner:{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.6rem', background:'rgba(52,211,153,.08)', border:'1px solid rgba(52,211,153,.35)', borderRadius:'0.8rem', padding:'0.65rem 0.85rem', margin:'0.2rem 0', flexWrap:'wrap' },
  recoverBtns:{ display:'flex', gap:'0.45rem', flexShrink:0 },
  discardBtn:{ background:'transparent', border:'1px solid rgba(255,255,255,.2)', color:'rgba(240,244,248,.6)', fontSize:'0.8rem', borderRadius:'0.55rem', padding:'0.45rem 0.7rem', cursor:'pointer' },
  privacy:   { color:'rgba(240,244,248,.4)', fontSize:'0.72rem', margin:'0.8rem 0 0', lineHeight:1.5 },
  linkBtn:   { display:'block', width:'100%', marginTop:'0.7rem', background:'transparent', border:'1px solid rgba(255,255,255,.14)', color:'rgba(240,244,248,.75)', fontSize:'0.82rem', fontWeight:600, borderRadius:'0.6rem', padding:'0.6rem 0.8rem', cursor:'pointer' },
  repairBtn: { display:'block', width:'100%', marginTop:'0.9rem', background:'rgba(139,92,246,.14)', border:'1px solid rgba(139,92,246,.34)', color:'#e6ddff', fontSize:'0.88rem', fontWeight:700, borderRadius:'0.7rem', padding:'0.7rem 1rem', cursor:'pointer' },
  repairCard:{ marginTop:'0.9rem', background:'rgba(255,255,255,.04)', border:'1px solid rgba(139,92,246,.28)', borderRadius:'0.9rem', padding:'0.9rem' },
  repairText:{ margin:'0 0 0.6rem', fontSize:'0.95rem', lineHeight:1.6, color:'#f4f1ff', whiteSpace:'pre-line' },
  copyBtn:   { background:'transparent', border:'1px solid rgba(255,255,255,.2)', color:'#e8eef4', fontSize:'0.8rem', fontWeight:600, borderRadius:'0.5rem', padding:'0.4rem 0.9rem', cursor:'pointer' },
  welcomeBanner:{ display:'flex', alignItems:'flex-start', gap:'0.6rem', background:'rgba(139,92,246,.1)', border:'1px solid rgba(139,92,246,.28)', borderRadius:'0.8rem', padding:'0.7rem 0.9rem', margin:'0.2rem 0' },
  welcomeText:  { flex:1, fontSize:'0.88rem', lineHeight:1.5, color:'#e6ddff' },
  welcomeClose: { background:'transparent', border:'none', color:'rgba(240,244,248,.5)', fontSize:'1.2rem', lineHeight:1, cursor:'pointer', flexShrink:0 },
  starters:     { marginTop:'0.8rem' },
  startersLabel:{ display:'block', fontSize:'0.72rem', color:'rgba(240,244,248,.42)', marginBottom:'0.45rem' },
  starterRow:   { display:'flex', flexWrap:'wrap', gap:'0.4rem' },
  starterChip:  { fontSize:'0.78rem', fontWeight:600, color:'rgba(240,244,248,.78)', background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.14)', borderRadius:'1rem', padding:'0.34rem 0.75rem', cursor:'pointer' },
  sessionMode:{ position:'fixed', inset:0, zIndex:1000, background:'radial-gradient(circle at 50% 40%, #0b0f18, #04060a)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'0.9rem', padding:'2rem' },
  pulseDot:  { width:16, height:16, borderRadius:'50%', background:'#f0655f', boxShadow:'0 0 24px 6px rgba(240,101,95,.45)', animation:'sovpulse 1.9s ease-in-out infinite' },
  listening: { margin:0, fontSize:'1rem', letterSpacing:'0.08em', color:'rgba(240,244,248,.8)' },
  timer:     { margin:0, fontSize:'2.6rem', fontWeight:300, color:'#f0f4f8', fontVariantNumeric:'tabular-nums' },
  sessionHint:{ margin:'0.3rem 0 0.6rem', fontSize:'0.82rem', lineHeight:1.55, color:'rgba(240,244,248,.45)', maxWidth:300, textAlign:'center' },
  stopBig:   { background:'rgba(239,68,68,.92)', border:'none', color:'#fff', fontSize:'1rem', fontWeight:700, borderRadius:'999px', padding:'0.9rem 2.3rem', cursor:'pointer' },
  reflection:{ margin:0, fontSize:'1.02rem', lineHeight:1.65, color:'#f4f1ff', whiteSpace:'pre-line' },
  block:     { marginTop:'1.1rem' },
  label:     { fontSize:'0.64rem', letterSpacing:'0.14em', textTransform:'uppercase', color:'rgba(240,244,248,.4)', fontWeight:700, margin:'0 0 0.4rem' },
  summary:   { margin:0, fontSize:'0.9rem', lineHeight:1.6, color:'rgba(240,244,248,.8)' },
  chips:     { display:'flex', flexWrap:'wrap', gap:'0.4rem' },
  chip:      { fontSize:'0.78rem', fontWeight:600, color:'#d9ccff', background:'rgba(139,92,246,.16)', border:'1px solid rgba(139,92,246,.32)', borderRadius:'1rem', padding:'0.28rem 0.7rem' },
  moments:   { margin:0, paddingLeft:'1.1rem', display:'flex', flexDirection:'column', gap:'0.3rem' },
  moment:    { fontSize:'0.88rem', lineHeight:1.5, color:'rgba(240,244,248,.8)' },
  crisisCard:{ background:'rgba(239,68,68,.08)', border:'1px solid rgba(239,68,68,.35)', borderRadius:'1rem', padding:'1.1rem', marginTop:'0.6rem' },
  crisisText:{ margin:0, fontSize:'0.98rem', lineHeight:1.65, color:'#ffe9e9', whiteSpace:'pre-line' },
  resources: { display:'flex', flexDirection:'column', gap:'0.4rem', margin:'0.9rem 0' },
  resource:  { fontSize:'0.9rem', fontWeight:600, color:'#fff', background:'rgba(239,68,68,.16)', borderRadius:'0.6rem', padding:'0.55rem 0.7rem' },
  consentEntry:{ width:'100%', display:'flex', gap:'0.7rem', alignItems:'flex-start', textAlign:'left', background:'rgba(255,255,255,.03)', border:'1px dashed rgba(255,255,255,.16)', borderRadius:'1rem', padding:'0.9rem 1rem', marginTop:'0.8rem', cursor:'pointer', color:'#e8eef4', fontFamily:'inherit' },
  consentEntryIcon:{ fontSize:'1.3rem', lineHeight:1.2 },
  consentEntryTitle:{ display:'block', fontSize:'0.92rem', fontWeight:700, color:'#f0f4f8', marginBottom:'0.15rem' },
  consentEntrySub:{ display:'block', fontSize:'0.78rem', lineHeight:1.5, color:'rgba(240,244,248,.5)' },
}
